/**
 * PricingDisplay Component
 *
 * MVP 1.22 - Experience Pricing & Promotions Engine
 * Story: SCRUM-372 - View Experience Pricing
 *
 * Customer-facing pricing display with real-time updates.
 * Shows structured breakdown with base price, modifiers, add-ons, and platform fee.
 *
 * Acceptance Criteria:
 * - AC-001.1: Pricing updates within 500ms when inputs change
 * - AC-001.2: Price breakdown shows base, adjustments, add-ons, platform fee separately
 * - AC-001.3: Seasonal/weekend/holiday premiums clearly labeled with explanation
 * - AC-001.5: Price displays in local currency with proper formatting
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { calculatePricing } from '../../services/pricingApi';
import { formatCentsToDollars } from '../../utils/formHelpers';

/**
 * Skeleton loader for pricing display
 */
const PricingSkeleton = () => (
  <div className="animate-pulse space-y-3">
    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
    <div className="h-6 bg-gray-200 rounded w-1/3 mt-4"></div>
  </div>
);

/**
 * Line item component for individual price breakdown items
 */
const LineItem = ({ label, amount, isDiscount, isTotal, tooltip }) => {
  const textColor = isDiscount ? 'text-green-600' : 'text-gray-700';
  const fontWeight = isTotal ? 'font-semibold text-lg' : 'font-normal text-sm';

  return (
    <div className={`flex justify-between items-center py-1 ${isTotal ? 'pt-3 border-t border-gray-200' : ''}`}>
      <span className={`${fontWeight} ${isDiscount ? 'text-green-600' : 'text-gray-600'}`}>
        {label}
        {tooltip && (
          <span className="ml-1 inline-block">
            <span
              className="text-gray-400 cursor-help"
              title={tooltip}
            >
              <svg
                className="w-4 h-4 inline"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </span>
        )}
      </span>
      <span className={`${fontWeight} ${textColor}`}>
        {isDiscount && amount > 0 ? '-' : ''}{formatCentsToDollars(Math.abs(amount))}
      </span>
    </div>
  );
};

LineItem.propTypes = {
  label: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  isDiscount: PropTypes.bool,
  isTotal: PropTypes.bool,
  tooltip: PropTypes.string
};

/**
 * Modifier badge component for premiums/discounts
 */
const ModifierBadge = ({ modifier }) => {
  const isDiscount = modifier.amountCents < 0;
  const bgColor = isDiscount ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800';

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${bgColor}`}>
      {modifier.percentage
        ? `${modifier.percentage > 0 ? '+' : ''}${modifier.percentage}%`
        : `${modifier.amountCents > 0 ? '+' : ''}${formatCentsToDollars(modifier.amountCents)}`}
    </span>
  );
};

ModifierBadge.propTypes = {
  modifier: PropTypes.shape({
    amountCents: PropTypes.number.isRequired,
    percentage: PropTypes.number
  }).isRequired
};

/**
 * PricingDisplay Component
 *
 * Displays real-time pricing breakdown for an experience.
 */
const EMPTY_ADDON_IDS = [];

const PricingDisplay = ({
  experienceId,
  date,
  partySize = 2,
  addonIds = EMPTY_ADDON_IDS,
  promoCode,
  onPricingCalculated,
  className = ''
}) => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debounce timer ref
  const debounceTimer = useRef(null);

  // Refs for values that change identity on every render but shouldn't retrigger fetches
  const addonIdsRef = useRef(addonIds);
  addonIdsRef.current = addonIds;
  const onPricingCalculatedRef = useRef(onPricingCalculated);
  onPricingCalculatedRef.current = onPricingCalculated;

  // Stable key for addonIds — only changes when actual values change
  const addonIdsKey = JSON.stringify(addonIds);

  // Fetch pricing with debounce (AC-001.1: updates within 500ms)
  const fetchPricing = useCallback(async () => {
    if (!experienceId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await calculatePricing(experienceId, {
        date,
        partySize,
        addonIds: addonIdsRef.current,
        promoCode
      });

      if (response.success) {
        setPricing(response.data);
        if (onPricingCalculatedRef.current) {
          onPricingCalculatedRef.current(response.data);
        }
      } else {
        setError(response.error?.message || 'Failed to calculate pricing');
      }
    } catch (err) {
      setError(err.message || 'Failed to calculate pricing');
    } finally {
      setLoading(false);
    }
  }, [experienceId, date, partySize, addonIdsKey, promoCode]);

  // Debounced fetch when inputs change
  useEffect(() => {
    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer (150ms debounce for fast perceived response)
    debounceTimer.current = setTimeout(() => {
      fetchPricing();
    }, 150);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [fetchPricing]);

  // Loading state
  if (loading && !pricing) {
    return (
      <div className={`bg-gray-50 rounded-lg p-4 ${className}`}>
        <h3 className="font-semibold text-gray-900 mb-3">Pricing</h3>
        <PricingSkeleton />
      </div>
    );
  }

  // Error state
  if (error && !pricing) {
    return (
      <div className={`bg-gray-50 rounded-lg p-4 ${className}`}>
        <h3 className="font-semibold text-gray-900 mb-3">Pricing</h3>
        <p className="text-gray-500 text-sm">
          {error === 'No active pricing configuration' || error.includes('pricing configuration')
            ? 'Contact guide for pricing'
            : 'Unable to load pricing. Please try again.'}
        </p>
      </div>
    );
  }

  // No pricing data
  if (!pricing) {
    return (
      <div className={`bg-gray-50 rounded-lg p-4 ${className}`}>
        <h3 className="font-semibold text-gray-900 mb-3">Pricing</h3>
        <p className="text-gray-500 text-sm">Contact guide for pricing</p>
      </div>
    );
  }

  const hasModifiers = pricing.modifiers && pricing.modifiers.length > 0;
  const hasAddons = pricing.addons && pricing.addons.length > 0;
  const hasPromotion = pricing.promotion;

  return (
    <div className={`bg-gray-50 rounded-lg p-4 ${className} ${loading ? 'opacity-75' : ''}`}>
      <h3 className="font-semibold text-gray-900 mb-3">Pricing Summary</h3>

      {/* Base Price */}
      <LineItem
        label={`Base price (${partySize} ${partySize === 1 ? 'guest' : 'guests'})`}
        amount={pricing.baseRateCents}
      />

      {/* Modifiers (AC-001.3: clearly labeled with explanation) */}
      {hasModifiers && (
        <div className="mt-2 space-y-1">
          {pricing.modifiers.map((modifier, index) => (
            <div key={index} className="flex justify-between items-center py-1">
              <span className="text-sm text-gray-600 flex items-center gap-2">
                {modifier.label}
                <ModifierBadge modifier={modifier} />
              </span>
              <span className={`text-sm ${modifier.amountCents < 0 ? 'text-green-600' : 'text-gray-700'}`}>
                {modifier.amountCents > 0 ? '+' : ''}{formatCentsToDollars(modifier.amountCents)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Add-ons */}
      {hasAddons && (
        <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
          {pricing.addons.map((addon, index) => (
            <LineItem
              key={index}
              label={`${addon.name}${addon.quantity > 1 ? ` (x${addon.quantity})` : ''}`}
              amount={addon.lineTotalCents}
            />
          ))}
        </div>
      )}

      {/* Promotion Discount */}
      {hasPromotion && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <LineItem
            label={`Promo: ${pricing.promotion.code}`}
            amount={pricing.promotionDiscountCents}
            isDiscount
          />
        </div>
      )}

      {/* Subtotal (if there are modifiers, add-ons, or promos) */}
      {(hasModifiers || hasAddons || hasPromotion) && (
        <LineItem
          label="Subtotal"
          amount={pricing.subtotalCents}
        />
      )}

      {/* Platform Fee */}
      <LineItem
        label={`Platform fee (${pricing.platformFeePercent}%)`}
        amount={pricing.platformFeeCents}
        tooltip="This fee supports the OutdoorGuides platform and helps us provide 24/7 customer support, secure payments, and trip protection."
      />

      {/* Total */}
      <LineItem
        label="Total"
        amount={pricing.totalCents}
        isTotal
      />

      {/* Per-person breakdown */}
      {partySize > 1 && (
        <p className="text-xs text-gray-500 mt-2 text-right">
          {pricing.display?.pricePerPerson || formatCentsToDollars(Math.round(pricing.totalCents / partySize))} per person
        </p>
      )}
    </div>
  );
};

PricingDisplay.propTypes = {
  experienceId: PropTypes.string.isRequired,
  date: PropTypes.string,
  partySize: PropTypes.number,
  addonIds: PropTypes.arrayOf(PropTypes.string),
  promoCode: PropTypes.string,
  onPricingCalculated: PropTypes.func,
  className: PropTypes.string
};

export default PricingDisplay;
